import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { formatUnits } from 'viem'

export function formatCompactNumber(n: number): string {
  if (n === 0) return '0'
  if (n < 0.001) return '<0.001'
  if (n < 1) return n.toLocaleString('en-US', { maximumFractionDigits: 3 })
  if (n >= 1000) {
    return n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 2 })
  }
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

export function decodeCapValue(encoded: number): number {
  if (encoded === 0) return 0
  const exponent = encoded & 0x3f
  const mantissa = (encoded >> 6) / 100
  return mantissa * 10 ** exponent
}

export function decodedToWei(value: number): bigint {
  if (value === 0) return 0n
  const str = value.toString()
  if (str.includes('e') || str.includes('E')) {
    const parts = str.toLowerCase().split('e')
    const base = parts[0] || '0'
    const exp = Number.parseInt(parts[1] || '0', 10)
    const [intPart = '0', decPart = ''] = base.split('.')
    if (exp > 0) {
      return exp >= decPart.length
        ? BigInt(intPart + decPart + '0'.repeat(exp - decPart.length))
        : BigInt(intPart + decPart.slice(0, exp))
    }
    return BigInt(intPart)
  }
  const [integerPart = '0'] = str.split('.')
  return BigInt(integerPart)
}

/** Compute utilization % using bigint arithmetic to avoid floating-point precision loss. */
export function computeUtilizationPct(total: bigint, cap: bigint): number {
  if (cap === 0n) return 0
  const bps = (total * 10000n) / cap
  const wholePart = Number(bps / 100n)
  const decimalPart = Number(bps % 100n)
  return Number.parseFloat(`${wholePart}.${decimalPart.toString().padStart(2, '0')}`)
}

export interface CapInfo {
  isUnlimited: false
  remaining: NormalizedUnitNumber
  pct: number
}

export type CapResult = { isLoading: true } | { isUnlimited: true } | CapInfo

export function computeCapInfo(
  encodedCap: number | undefined,
  totalRaw: bigint | undefined,
  capsLoaded: boolean,
  decimals: number,
): CapResult {
  if (!capsLoaded) return { isLoading: true }
  if (!encodedCap) return { isUnlimited: true }

  const decoded = decodeCapValue(encodedCap)
  if (decoded === 0) return { isUnlimited: true }

  const capBigInt = decodedToWei(decoded)
  const totalBigInt = totalRaw ?? 0n

  const remainingBigInt = capBigInt > totalBigInt ? capBigInt - totalBigInt : 0n
  const remaining = NormalizedUnitNumber(formatUnits(remainingBigInt, decimals))
  const pct = computeUtilizationPct(totalBigInt, capBigInt)

  return { isUnlimited: false, remaining, pct }
}
